'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { isAdFreePath } from '../../../../lib/freestar-ad-free-paths';
import { registerAdFreePages } from '../utils/ad-free-registry';

interface FreestarProduct {
	disabled?: boolean;
}

interface FreestarGlobal {
	queue?: { push: (callback: () => void) => void };
	config?: { products?: Record<string, FreestarProduct | undefined> };
	deleteFlyingCarpet?: () => void;
	deletePushdown?: () => void;
	deleteSidewall?: () => void;
	deleteStandAlonePlayer?: () => void;
	deleteStickyFooter?: () => void;
	deleteStickyRail?: () => void;
	newStickyFooter?: () => void;
}

interface FreestarRouteGuardProps {
	adFreePages: string[];
	/**
	 * Webview clients take no pubfig products on any page, not just the ad-free ones.
	 *
	 * Deliberately narrower than `HeaderScripts`' `adFree`, which withholds pubfig altogether and so
	 * would take the declared slots down with it. Those stay monetised in a webview; only the
	 * products pubfig injects on its own are suppressed.
	 */
	isWebview?: boolean;
}

function getFreestar(): FreestarGlobal | undefined {
	if (typeof window === 'undefined') return undefined;

	return (window as unknown as { freestar?: FreestarGlobal }).freestar;
}

// A throwing vendor call must not take the rest of the teardown with it.
function attempt(action: (() => void) | undefined) {
	try {
		action?.();
	} catch {
		// Product was never instantiated, or pubfig changed its API. Nothing to do either way.
	}
}

/**
 * Keeps Freestar *products* — sticky footer, sidewall, pushdown and friends — off the pages
 * listed in `adFreePages`.
 *
 * Slots need no help here: they are React components, and the Freestar adslot package deletes
 * each one on unmount. Products are different. pubfig owns them, mounts them straight onto
 * `document.body`, and they survive client-side navigation — a sticky footer picked up on
 * /results is still there after soft-navigating into a score entry page.
 *
 * `HeaderScripts` already keeps pubfig out of the document on a direct hit to an ad-free page.
 * This covers the other route in: arriving by client-side navigation, where pubfig is already
 * loaded and there is no second server render to gate.
 *
 * `isWebview` applies the same teardown to every page. A webview keeps its declared slots — killing
 * pubfig outright would lose those too — so the products are the only thing taken away.
 *
 * The callback re-reads the pathname at execution time rather than closing over it. Callbacks
 * queued before pubfig loads only run once it does, by which point the user may have navigated
 * again, and a stale verdict would disable products on a page that should have them.
 */
export const FreestarRouteGuard = ({ adFreePages, isWebview = false }: FreestarRouteGuardProps) => {
	const pathname = usePathname();
	const productsTornDown = useRef(false);

	// Publish the list for the client slot components. Done during render, not in an effect, so it
	// is set before anything below this in the tree renders — the root layout mounts this first.
	// Idempotent: the same per-app constant every time.
	registerAdFreePages(adFreePages);

	useEffect(() => {
		const freestar = getFreestar();

		// No bootstrap means Freestar is off entirely (DEV/TEST), so there is nothing to guard.
		if (!freestar?.queue) return;

		freestar.queue.push(() => {
			// Webview short-circuits the path check: every page is product-free there. Because this
			// stays true for the life of the session, the restore branch below is unreachable and
			// nothing is ever put back.
			const adFree = isWebview || isAdFreePath(window.location.pathname, adFreePages);
			const products = freestar.config?.products;

			// Live kill switch: pubfig honours this flag when it creates a product, so flipping it
			// stops anything from coming back while we are on an ad-free page.
			if (products) {
				Object.keys(products).forEach((key) => {
					const product = products[key];
					if (product) product.disabled = adFree;
				});
			}

			if (adFree) {
				productsTornDown.current = true;

				attempt(freestar.deleteStickyFooter);
				attempt(freestar.deleteStickyRail);
				attempt(freestar.deletePushdown);
				attempt(freestar.deleteSidewall);
				attempt(freestar.deleteFlyingCarpet);
				attempt(freestar.deleteStandAlonePlayer);

				return;
			}

			// Back on a monetised page. pubfig does not re-create what we tore down, so put the
			// sticky footer back — but only if we were the ones who removed it, otherwise we would
			// race pubfig's own first pass and risk a duplicate.
			if (productsTornDown.current) {
				productsTornDown.current = false;
				attempt(freestar.newStickyFooter);
			}
		});
	}, [pathname, adFreePages, isWebview]);

	return null;
};
